/* One measured scroll frame per visible chapter. All essential copy is in Liquid. */
(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = value => { const x = clamp(value); return x * x * (3 - 2 * x); };
  class ProductJourney extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-journey-step]')];
      this.pieces = Object.fromEntries([...this.querySelectorAll('[data-journey-piece]')].map(el => [el.dataset.journeyPiece, el]));
      this.video = this.querySelector('[data-journey-video]');
      this.playButton = this.querySelector('[data-journey-play]');
      this.playLabel = this.querySelector('[data-journey-play-label]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.hynoxModel = this.querySelector('[data-hynox-model]');
      if (this.hynoxModel) {
        this.stage = this.querySelector('.hj-stage');
        this.phase = this.querySelector('[data-hynox-phase]');
        this.index = this.querySelector('[data-hynox-index]');
        this.progressBar = this.querySelector('[data-hynox-progress]');
        this.resize = new ResizeObserver(() => { this.scene?.resize(); this.schedule?.(); });
        this.resize.observe(this.stage);
      }
      this.filmProgress = 0;
      const options = { passive: true, signal: this.abort.signal };
      this.schedule = () => { if (this.near && !this.frame && !document.hidden) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      addEventListener('scroll', this.schedule, options);
      addEventListener('resize', this.schedule, options);
      document.addEventListener('visibilitychange', this.schedule, { signal: this.abort.signal });
      this.motion.addEventListener('change', () => {
        if (this.hynoxModel) {
          if (this.motion.matches) { this.scene?.dispose(); this.scene = null; this.classList.remove('is-3d'); }
          else if (this.near) this.loadScene();
        }
        this.schedule();
      }, { signal: this.abort.signal });
      this.observer = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near) { this.loadFilm(); if (this.hynoxModel && !this.motion.matches) this.loadScene(); this.schedule(); } else this.video?.pause();
      }, { rootMargin: '300px' });
      this.observer.observe(this);
      if (this.video) {
        this.video.addEventListener('loadedmetadata', this.schedule, { signal: this.abort.signal });
        this.video.addEventListener('seeked', () => { if (!this.manualPlayback) this.seekFilm(); }, { signal: this.abort.signal });
        this.video.addEventListener('ended', () => { this.manualPlayback = false; this.playLabel.textContent = 'Relire l’animation'; }, { signal: this.abort.signal });
        this.playButton.hidden = false;
        this.playButton.addEventListener('click', () => {
          if (this.manualPlayback) { this.video.pause(); this.manualPlayback = false; this.playLabel.textContent = 'Lire l’animation'; this.schedule(); return; }
          this.manualPlayback = true;
          this.classList.add('is-film');
          this.video.controls = true;
          this.video.currentTime = 0;
          this.video.play().then(() => { this.playLabel.textContent = 'Reprendre au défilement'; }).catch(() => { this.manualPlayback = false; });
        }, { signal: this.abort.signal });
      }
      this.classList.add('is-enhanced');
    }
    async loadScene() {
      if (this.scene || this.loading || this.failed) return;
      this.loading = true;
      try {
        const { createHynoxScene } = await import(this.dataset.sceneUrl);
        if (!this.isConnected || this.motion.matches) return;
        const scene = await createHynoxScene(this.hynoxModel, this.dataset.engineUrl, { modelUrl: this.dataset.modelUrl, filterUrl: this.dataset.filterUrl });
        if (!this.isConnected || this.motion.matches) { scene.dispose(); return; }
        this.scene = scene;
        this.classList.add('is-3d'); this.schedule();
      } catch { this.failed = true; this.classList.add('is-fallback'); }
      finally { this.loading = false; }
    }
    loadFilm() {
      const source = this.video?.querySelector('[data-src]');
      if (source) { source.src = source.dataset.src; source.removeAttribute('data-src'); this.video.preload = 'auto'; this.video.load(); }
    }
    seekFilm() {
      if (!this.video || this.manualPlayback || this.motion.matches || this.video.seeking || !Number.isFinite(this.video.duration)) return;
      const time = this.filmProgress * Math.max(0, this.video.duration - 0.05);
      if (Math.abs(this.video.currentTime - time) > 0.035) this.video.currentTime = time;
    }
    update() {
      const rect = this.getBoundingClientRect();
      const viewport = innerHeight;
      const center = viewport * (innerWidth < 750 ? 0.75 : 0.53);
      const stepRects = this.steps.map(el => el.getBoundingClientRect());
      let active = 0;
      stepRects.forEach((r, i) => { if (r.top < center) active = i; });
      this.steps.forEach((el, i) => { el.classList.toggle('is-active', i === active); if (i === active) el.setAttribute('aria-current', 'step'); else el.removeAttribute('aria-current'); });
      if (this.motion.matches) {
        Object.values(this.pieces).forEach(el => { el.removeAttribute('transform'); el.style.opacity = ''; });
        const product = this.querySelector('.hj-product');
        if (product) product.style.transform = '';
        this.classList.remove('is-film');
        this.video?.pause();
        if (this.video) this.video.controls = true;
        return;
      }
      const p = clamp((center - rect.top) / Math.max(1, rect.height - viewport * 0.45));
      if (this.hynoxModel) {
        const readingLine = innerWidth < 750 ? Math.min(viewport * .8, this.stage.getBoundingClientRect().bottom + 155) : viewport * .55;
        const first = stepRects[0].top + stepRects[0].height * .5;
        const last = stepRects[3].top + stepRects[3].height * .5;
        const progress = clamp((readingLine - first) / Math.max(1, last - first));
        const current = progress < .30 ? 0 : progress < .50 ? 1 : progress < .80 ? 2 : 3;
        this.steps.forEach((el, i) => { el.classList.toggle('is-active', i === current); if (i === current) el.setAttribute('aria-current', 'step'); else el.removeAttribute('aria-current'); });
        this.phase.textContent = this.steps[current].dataset.hynoxLabel;
        this.index.textContent = `0${current + 1} / 04`;
        this.progressBar.style.transform = `scaleX(${progress})`;
        this.dataset.hynoxProgress = progress.toFixed(3);
        this.targetProgress = progress;
        this.animateScene();
      } else {
        const close = smooth(p / 0.43);
        this.pieces.cap.setAttribute('transform', `translate(0 ${-115*(1-close)}) rotate(${12*(1-close)} 625 300)`);
        this.querySelector('.hj-product').style.transform = `scale(${1.15 - close * 0.15})`;
        this.filmProgress = clamp((p - 0.46) / 0.48);
        this.classList.toggle('is-film', this.manualPlayback || p > 0.46);
        if (!this.manualPlayback) { this.video.controls = false; this.seekFilm(); }
      }
    }
    animateScene() {
      if (!this.scene || this.motion.matches || this.motionFrame || !this.near || document.hidden) return;
      const tick = now => {
        this.motionFrame = 0;
        if (!this.isConnected || !this.near || document.hidden || this.motion.matches) { this.motionTime = 0; return; }
        const target = this.targetProgress ?? 0;
        if (this.currentProgress == null) this.currentProgress = target;
        const dt = this.motionTime ? Math.min((now - this.motionTime) / 1000, .05) : 1 / 60;
        this.motionTime = now;
        this.currentProgress += (target - this.currentProgress) * (1 - Math.exp(-dt / .10));
        if (Math.abs(target - this.currentProgress) < .0001) this.currentProgress = target;
        this.scene?.render(this.currentProgress);
        if (this.currentProgress !== target) this.motionFrame = requestAnimationFrame(tick);
        else this.motionTime = 0;
      };
      this.motionFrame = requestAnimationFrame(tick);
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); cancelAnimationFrame(this.motionFrame); this.video?.pause(); this.resize?.disconnect(); this.scene?.dispose(); this.scene = null; }
  }
  if (!customElements.get('hydros-product-journey')) customElements.define('hydros-product-journey', ProductJourney);
  // Holding a photo shows its alternate view; vertical swiping remains native.
  document.querySelectorAll('.hs-shop .ecom-product-card__media').forEach(link => {
    const restore = () => link.classList.remove('is-touch-preview');
    link.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch') return;
      link.classList.add('is-touch-preview');
    }, { passive: true });
    link.addEventListener('pointerup', restore, { passive: true });
    link.addEventListener('pointercancel', restore, { passive: true });
  });
})();
